import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import {
  getCodeData,
  getInitForm,
  getInitSheet,
} from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import SnSheet from "@shared/components/Grid/SnSheet";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizableLNR from "@shared/components/SnResizableLNR";
import SnButton from "@shared/components/SnButton";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { IconButton, Tooltip } from "@mui/material";

interface Props {
  Menuprops: menuInfo;
  PkFields: string[]; // PK 필드 목록
  tableName: string; // 저장할 테이블 이름
  controllerName: string; // 저장할 API 컨트롤러 이름
  pkCheckCode?: string; // PK 중복 체크에 사용할 코드/테이블명
  defaultValue?: Record<string, any>;
}

const ExcelUpload = forwardRef(
  (
    {
      Menuprops,
      PkFields,
      tableName,
      controllerName,
      pkCheckCode,
      defaultValue,
    }: Props,
    ref: React.Ref<any>
  ) => {
    const [columns, setColums] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any>();
    const [statusMessage, setStatusMessage] = useState<string>("");
    const gridref1 = useRef<any>(null);
    const codeDataCacheRef = useRef<Map<string, any[]>>(new Map());
    const popupDataCacheRef = useRef<Map<string, any[]>>(new Map());
    let SaveData: any = {};

    //#region grid 길이 관련
    let width1 = "99%";
    const menuSize = localStorage.getItem("menuSize");
    if (menuSize) {
      const menu = JSON.parse(menuSize);
      if (menu.ExcelUpload) {
        width1 = menu.ExcelUpload.grid1Width
          ? menu.ExcelUpload.grid1Width
          : width1;
      }
    }
    const [grid1Width, setGrid1Width] = useState<string | number>(width1);

    const [compareFields, setCompareFields] = useState<string[]>(PkFields); // 비교할 필드를 상태로 저장
    const [codeDataList, setCodeDataList] = useState<Record<string, string>[]>(
      []
    ); // 비교 대상 테이블 data tableName

    //  tableName list

    //  메뉴에서 `cd_clas`, `cd_popup` 데이터를 한 번만 로드
    const [codeDataMap, setCodeDataMap] = useState(new Map());
    const [popupDataMap, setPopupDataMap] = useState(new Map());

    //#endregion

    useImperativeHandle(ref, () => ({
      onDelete,
      onSave,
      getSelectedValue: async () => {
        const ok = await onSave();
        return { excelUpload: !!ok };
      },
      getSelectedRow: () => {
        const gridData: any[] = [];
        gridref1.current.api.forEachNode((node: any) => {
          gridData.push(node.data);
        });
        return gridData;
      },
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitSheet({});

          setColums(rtn.grid.sheet1);

          gridref1.current.resetGridData();
        } catch (error) {}
      };
      fetchform();
    }, []);

    useEffect(() => {
      if (gridref1.current) {
        gridref1.current.setCodeDataList(codeDataList); //  SnSheet 내부에서 코드 데이터 갱신 메서드 실행
      }
    }, [codeDataList]); //  codeDataList가 변경될 때마다 실행

    const onDelete = () => {
      const focusedCell = gridref1.current.api.getFocusedCell();
      let startRowIndex = focusedCell.rowIndex ?? 0;

      gridref1.current?.deleteRowByIndex(startRowIndex);
    };

    const runWithStatus = async <T,>(
      message: string,
      task: () => Promise<T>
    ): Promise<T> => {
      setStatusMessage(message);
      try {
        setStatusMessage("유효성 확인 중...");
        return await task();
      } finally {
        setStatusMessage("");
      }
    };

    const setOverlayMessage = (message: string) => {
      const api = gridref1.current?.api;
      if (!api) return;
      (api as any).__loadingMessage = message;
      const template = `<span class="ag-overlay-loading-center">${message}</span>`;
      api.setGridOption?.("overlayLoadingTemplate", template);
      api.setOverlayLoadingTemplate?.(template);
    };

    const normalizeDateValue = (value: unknown) => {
      if (typeof value !== "string") return value;
      const trimmed = value.trim();
      if (!trimmed) return value;
      return trimmed.replace(/[^\d]/g, "");
    };

    const normalizeRowForSave = (
      row: Record<string, any>,
      codeMap: Map<string, any[]>,
      popupMap: Map<string, any[]>
    ) => {
      const next = { ...row };
      const firm = global.getState().userInfo?.cd_firm;
      if (
        firm &&
        (next.cd_firm == null || String(next.cd_firm).trim() === "")
      ) {
        next.cd_firm = firm;
      }
      Object.keys(next).forEach((key) => {
        if (key.startsWith("dt_")) {
          next[key] = normalizeDateValue(next[key]);
        }
      });
      (columns ?? []).forEach((col: any) => {
        const field = String(col.cd_column ?? "").trim();
        if (!field || next[field] == null || next[field] === "") return;

        if (col.tp_column === "SnComboBox" && col.cd_clas) {
          const list = codeMap.get(col.cd_clas) ?? [];
          const match = list.find(
            (item: any) =>
              item.code === next[field] || item.name === next[field]
          );
          if (match) {
            next[field] = match.code;
            const displayField =
              col.cd_displaycolumn ||
              (field.startsWith("cd_") ? field.replace(/^cd_/, "nm_") : "");
            if (displayField && displayField !== field) {
              next[displayField] = match.name;
            }
          }
        }

        if (col.tp_column === "SnCodeBox" && col.cd_popup) {
          const list = popupMap.get(col.cd_popup) ?? [];
          const match = list.find(
            (item: any) =>
              item.code === next[field] || item.name === next[field]
          );
          if (match) {
            next[field] = match.code;
            const displayField =
              col.cd_displaycolumn || field.replace(/^cd_/, "nm_");
            if (displayField && displayField !== field) {
              next[displayField] = match.name;
            }
          }
        }
      });
      return next;
    };

    const buildSavePayload = async (change: {
      insert?: any[];
      update?: any[];
      delete?: any[];
      modifiedRowcount?: number;
    }) => {
      const uniqueCdClas = Array.from(
        new Set(
          (columns ?? [])
            .filter((col: any) => col.cd_clas && col.tp_column === "SnComboBox")
            .map((col: any) => String(col.cd_clas))
        )
      );
      const uniqueCdPopup = Array.from(
        new Set(
          (columns ?? [])
            .filter((col: any) => col.cd_popup && col.tp_column === "SnCodeBox")
            .map((col: any) => String(col.cd_popup))
        )
      );
      const codeMap = new Map<string, any[]>();
      const popupMap = new Map<string, any[]>();
      for (const cd of uniqueCdClas) {
        if (!codeDataCacheRef.current.has(cd)) {
          codeDataCacheRef.current.set(cd, await getCodeData(cd, ""));
        }
        codeMap.set(cd, codeDataCacheRef.current.get(cd) ?? []);
      }
      for (const cd of uniqueCdPopup) {
        if (!popupDataCacheRef.current.has(cd)) {
          popupDataCacheRef.current.set(cd, await getCodeData(cd, ""));
        }
        popupMap.set(cd, popupDataCacheRef.current.get(cd) ?? []);
      }

      const insert = (change.insert ?? []).map((row) =>
        normalizeRowForSave(row, codeMap, popupMap)
      );
      const update = (change.update ?? []).map((row) =>
        normalizeRowForSave(row, codeMap, popupMap)
      );
      const del = (change.delete ?? []).map((row) =>
        normalizeRowForSave(row, codeMap, popupMap)
      );
      return {
        insert,
        update,
        delete: del,
        modifiedRowcount: insert.length + update.length + del.length,
      };
    };

    const collectNonEmptyRows = () => {
      const api = gridref1.current?.api;
      if (!api) return [];
      const fields = (columns ?? [])
        .map((col: any) => String(col.cd_column ?? "").trim())
        .filter((field) => field.length > 0);
      const hasValue = (value: any) =>
        value === 0 ||
        value === false ||
        (value != null && String(value).trim() !== "");
      const rows: any[] = [];
      api.forEachNode((node: any) => {
        const row = node?.data;
        if (!row) return;
        if (fields.length > 0) {
          if (fields.some((field) => hasValue(row[field]))) {
            rows.push({ ...row });
          }
        } else if (
          Object.entries(row).some(
            ([key, value]) => key !== "KeyField" && hasValue(value)
          )
        ) {
          rows.push({ ...row });
        }
      });
      return rows;
    };

    const onSave = async (): Promise<boolean> => {
      try {
        // 중앙 로딩 오버레이 문구만 사용
        gridref1.current?.setLoadingMessage?.("유효성 확인 중...");
        await new Promise((resolve) => setTimeout(resolve, 0));
        (gridref1.current?.api as any).__suppressLoadingOverlay = true;
        (gridref1.current?.api as any).__preserveChangesOnValidate = true;
        (gridref1.current?.api as any).__validateChangedOnly = true;

        const pkCheckKey = pkCheckCode ?? tableName;
        let change = gridref1.current.getChange();
        const fallbackRows =
          change.modifiedRowcount === 0 ? collectNonEmptyRows() : [];
        if (change.modifiedRowcount === 0 && fallbackRows.length === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return false;
        }
        if (change.modifiedRowcount === 0 && fallbackRows.length > 0) {
          if (defaultValue) {
            fallbackRows.forEach((row: any) => {
              Object.assign(row, defaultValue);
            });
          }
          change = {
            insert: fallbackRows,
            update: [],
            delete: [],
            modifiedRowcount: fallbackRows.length,
          };
        }

        const v1 = await Validation(false);
        if (!v1) return false;

        // const v2 = await Validation(true);
        // if (!v2) return false;
        (gridref1.current?.api as any).__suppressLoadingOverlay = false;
        (gridref1.current?.api as any).__preserveChangesOnValidate = false;
        (gridref1.current?.api as any).__validateChangedOnly = false;
        const recipegroupChanges: any[] = [];
        const recipeChanges: any[] = [];
        let line = 1;
        const unique = [...new Set(fallbackRows.map((item) => item.cd_group))];

        const result = unique.map((cd_group, index) => ({
          cd_group,
          line: index + 1,
        }));
        fallbackRows.forEach((row: any) => {
          const group = recipeChanges.filter(
            (item) => item.cd_group === row.cd_group
          );
          recipeChanges.push({
            cd_firm: global.getState().userInfo?.cd_firm,
            cd_biz: row.cd_biz,
            no_line: line,
            no_seq: group.length + 1,
            ...row,
          });
          line++;
        });
        const grouped = Object.values(
          fallbackRows.reduce((acc, row) => {
            const key = `${row.cd_item}_${row.cd_group}`;
            if (!acc[key]) {
              acc[key] = row;
            }
            return acc;
          }, {})
        );

        grouped.forEach((row: any) => {
          const group = result.find((item) => item.cd_group === row.cd_group);
          recipegroupChanges.push({
            cd_firm: global.getState().userInfo?.cd_firm,
            cd_biz: row.cd_biz,
            no_seq_group: group?.line,
            ...row,
          });
        });

        const SaveData = {
          recipegroup: { insert: recipegroupChanges },
          recipe: { insert: recipeChanges },
        };

        // 저장 API 호출
        const rtn = await fnSave("SN_PRD_BAS_REG_001_CHEM_SAVE_01", SaveData);
        if (!rtn) {
          gridref1.current?.api?.hideOverlay?.();
          return false;
        }
        gridref1.current.resetUpdate();
        gridref1.current?.api?.hideOverlay?.();
        return true;
      } catch (error: any) {}
      (gridref1.current?.api as any).__suppressLoadingOverlay = false;
      (gridref1.current?.api as any).__preserveChangesOnValidate = false;
      (gridref1.current?.api as any).__validateChangedOnly = false;
      gridref1.current?.api?.hideOverlay?.();
      return false;
    };

    // grid1의 width resize
    const onResizeStopH = (width: number) => {
      setGrid1Width(width);
    };

    const Validation = async (updatevalue: boolean): Promise<boolean> => {
      //  Validation 실행하여 오류 체크
      // debugger;
      const data = await getCodeData(pkCheckCode ?? tableName, ""); //  data insert update체크할 데이터 조회
      setCodeDataList(data); // 기존 데이터 목록 저장

      if (gridref1.current.api.getDisplayedRowCount() <= 1) {
        await showDialog("info", "변경된 내용이 없습니다.");
        return true;
      }

      const isValid = await gridref1.current.validateData(updatevalue);

      if (!isValid) {
        showDialog("error", "오류내역을 확인해주세요.");
        return false;
      } else {
        return true;
      }
    };

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "aButton_Init") {
        gridref1.current.resetGridData();
      }

      if (id == "aButton_Validation") {
        setStatusMessage("유효성 확인 중...");
        setOverlayMessage("유효성 확인 중...");
        const isValid = await Validation(false);
        setStatusMessage("");
        if (isValid) {
          showDialog("info", "유효성 체크가 완료되었습니다.");
        }
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
          <SnResizableLNR
            width={grid1Width}
            minWidth={130}
            onResizeStop={onResizeStopH}
            enable={{ right: true }}
            menuName={Menuprops.cd_menu ?? ""}
            defaultSize={{ grid2Width: grid1Width }}
          >
            <SnSheet
              ref={gridref1}
              enableAddRow={true}
              columnConfig={columns}
              rowData={dataSource1}
              isEditable={true} // 수정 가능여부
              codeDataList={codeDataList} // pk 체크용 데이터 전달
              compareFields={compareFields} // 비교할 필드 정보 전달
              title={Menuprops.nm_menu}
              header={
                <div>
                  <SnButton
                    text={"초기화"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Init");
                    }}
                  />
                  <SnButton
                    text={"오류체크"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Validation");
                    }}
                  />
                  {statusMessage && (
                    <span
                      style={{
                        marginLeft: 8,
                        color: "gray",
                        fontSize: 12,
                      }}
                    >
                      {statusMessage}
                    </span>
                  )}
                  {false && (
                    <IconButton
                      onClick={onSave}
                      sx={{
                        color: "gray", // 기본 색상
                        "&:hover": {
                          color: "blue", // hover 시 색상
                          transform: "scale(1.2)", // hover 시 크기 확대
                          transition: "all 0.3s ease", // 부드러운 애니메이션
                        },
                      }}
                    >
                      <SaveIcon />
                    </IconButton>
                  )}

                  <Tooltip title="Delete">
                    <IconButton
                      onClick={onDelete}
                      sx={{
                        color: "gray", // 기본 색상
                        "&:hover": {
                          color: "blue", // hover 시 색상
                          transform: "scale(1.2)", // hover 시 크기 확대
                          transition: "all 0.3s ease", // 부드러운 애니메이션
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              }
            />
          </SnResizableLNR>
        </div>
      </div>
    );
  }
);

export default ExcelUpload;
